import React, { useCallback, useEffect, useMemo } from 'react';
import { useController, Control } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import OTTDatePicker from '@ott/datepicker';
import { l10n } from '@ott/l10n';
import { useLayoutContext } from '@ott/layout-context';

import { trackingTypes } from '../../constants';
import usePrev from '../../hooks/usePrev';
import { FormState, RenderContext } from '../../types';
import { getDateDifferenceInDays } from '../../utilities';
import getMinMaxDates from '../../utilities/getMinMaxDates';

type Props = {
  destinationGmt?: number;
  triggerClassName: string;
  putOverlayToRoot: boolean;
  control: Control<FormState>;
  context: RenderContext;
  defaultCalendarOpened?: boolean;
  preventCloseCalendarOnOutsideClick: boolean;
  maxDays: number;
};

const meta = {};

const DatePicker: React.FC<Props> = (props) => {
  const {
    destinationGmt,
    triggerClassName,
    putOverlayToRoot,
    control,
    context,
    defaultCalendarOpened,
    preventCloseCalendarOnOutsideClick,
    maxDays,
  } = props;
  // preventCloseCalendarOnOutsideClick выпиливаем или оставляем после завершения теста SEO-8317
  const { isTouchDevice } = useLayoutContext();
  const { minDate, maxDate } = getMinMaxDates(destinationGmt);
  const dispatch = useDispatch();
  const {
    field: { value, onChange, onBlur },
  } = useController({
    name: 'dates',
    control,
  });

  const prevMaxDays = usePrev(maxDays);

  const selectDateWithTracking = useCallback(
    (startDate: Date, endDate: Date) => {
      onChange({ startDate, endDate });

      if (!endDate || !startDate) {
        return;
      }

      dispatch({
        type: trackingTypes.datesChanged,
        eventData: {
          dates: { startDate, endDate },
          context,
        },
      });
    },
    [onChange, dispatch, context]
  );

  const input = useMemo(
    () => ({
      value,
      onBlur,
      name: 'dates',
    }),
    [value, onBlur]
  );

  const childrenClassNames = useMemo(() => ({ input: triggerClassName }), [triggerClassName]);

  useEffect(() => {
    if (maxDays >= prevMaxDays) {
      return;
    }

    if (!value.startDate || !value.endDate) {
      return;
    }

    const differenceInDays = getDateDifferenceInDays({
      startDate: value.startDate,
      endDate: value.endDate,
    });

    if (maxDays >= differenceInDays) {
      return;
    }

    onChange({ startDate: null, endDate: null });
  }, [maxDays]);

  return (
    <OTTDatePicker
      onDateSelect={selectDateWithTracking}
      endDatePlaceholder={l10n('hotelsSearchForm.endDatePlaceholder')}
      isTouchDevice={isTouchDevice}
      minDate={minDate}
      maxDate={maxDate}
      maxDays={maxDays}
      maxDaysTooltipText={l10n('hotelsSearchForm.validate.outOfRangeTooltipText', {
        value: maxDays - 1,
      })}
      placeholder={l10n('datepicker.title')}
      defaultCalendarOpened={defaultCalendarOpened}
      childrenClassNames={childrenClassNames}
      closeAfterSelect={true}
      isMobileSupport={true}
      differentDates={true}
      showNights={true}
      withArrowIcon={true}
      preventCloseCalendarOnOutsideClick={preventCloseCalendarOnOutsideClick}
      usePortalOverlay={putOverlayToRoot}
      scrollToWeek={putOverlayToRoot}
      input={input}
      meta={meta}
    />
  );
};

export default DatePicker;
